/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  Attribute,
  AttributeValue,
  AttributeValueUpdated,
  CreateAttributeRequest,
  CreateAttributeValueRequest,
  DeleteAdminV1OrgAttributesIdPathParameters,
  DeleteAdminV1OrgAttributesIdValuesValueIdPathParameters,
  GetAdminV1OrgAttributes200,
  GetAdminV1OrgAttributesIdPathParameters,
  PatchAdminV1OrgAttributesIdPathParameters,
  PatchAdminV1OrgAttributesIdValuesReorderPathParameters,
  PatchAdminV1OrgAttributesIdValuesValueIdPathParameters,
  PostAdminV1OrgAttributesIdValuesPathParameters,
  PostAdminV1OrgAttributesIdValuesSuggestSkuCodesPathParameters,
  ReorderAttributeValuesRequest,
  SuggestSkuCodesResponse,
  UpdateAttributeRequest,
  UpdateAttributeValueRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary The attribute dictionary of the organization, with values
 */
export const getAdminV1OrgAttributes = (

 options?: SecondParameter<typeof customInstance<GetAdminV1OrgAttributes200>>,) => {
      return customInstance<GetAdminV1OrgAttributes200>(
      {url: `/admin/v1/org/attributes`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Create an attribute, optionally with its values
 */
export const postAdminV1OrgAttributes = (
    createAttributeRequest: BodyType<CreateAttributeRequest>,
 options?: SecondParameter<typeof customInstance<Attribute>>,) => {
      return customInstance<Attribute>(
      {url: `/admin/v1/org/attributes`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createAttributeRequest
    },
      options);
    }
  /**
 * @summary Get an attribute with its values
 */
export const getAdminV1OrgAttributesId = (
    { id }: GetAdminV1OrgAttributesIdPathParameters,
 options?: SecondParameter<typeof customInstance<Attribute>>,) => {
      return customInstance<Attribute>(
      {url: `/admin/v1/org/attributes/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Update an attribute. The key is immutable.
 */
export const patchAdminV1OrgAttributesId = (
    { id }: PatchAdminV1OrgAttributesIdPathParameters,
    updateAttributeRequest: BodyType<UpdateAttributeRequest>,
 options?: SecondParameter<typeof customInstance<Attribute>>,) => {
      return customInstance<Attribute>(
      {url: `/admin/v1/org/attributes/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateAttributeRequest
    },
      options);
    }
  /**
 * @summary Delete an attribute that no product type or product uses
 */
export const deleteAdminV1OrgAttributesId = (
    { id }: DeleteAdminV1OrgAttributesIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/attributes/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Add a value to a select or multiselect attribute
 */
export const postAdminV1OrgAttributesIdValues = (
    { id }: PostAdminV1OrgAttributesIdValuesPathParameters,
    createAttributeValueRequest: BodyType<CreateAttributeValueRequest>,
 options?: SecondParameter<typeof customInstance<AttributeValue>>,) => {
      return customInstance<AttributeValue>(
      {url: `/admin/v1/org/attributes/${id}/values`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createAttributeValueRequest
    },
      options);
    }
  /**
 * valueIds must list every value of the attribute; positions follow the order sent
 * @summary Reorder the values of an attribute in one call
 */
export const patchAdminV1OrgAttributesIdValuesReorder = (
    { id }: PatchAdminV1OrgAttributesIdValuesReorderPathParameters,
    reorderAttributeValuesRequest: BodyType<ReorderAttributeValuesRequest>,
 options?: SecondParameter<typeof customInstance<Attribute>>,) => {
      return customInstance<Attribute>(
      {url: `/admin/v1/org/attributes/${id}/values/reorder`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: reorderAttributeValuesRequest
    },
      options);
    }
  /**
 * Changing skuCode never rewrites existing SKUs; affectedVariantCount says how many generated variant SKUs still carry the old code so staff can regenerate
 * @summary Update the label, swatch, metadata, SKU code or position of a value. The stored value is immutable.
 */
export const patchAdminV1OrgAttributesIdValuesValueId = (
    { id, valueId }: PatchAdminV1OrgAttributesIdValuesValueIdPathParameters,
    updateAttributeValueRequest: BodyType<UpdateAttributeValueRequest>,
 options?: SecondParameter<typeof customInstance<AttributeValueUpdated>>,) => {
      return customInstance<AttributeValueUpdated>(
      {url: `/admin/v1/org/attributes/${id}/values/${valueId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateAttributeValueRequest
    },
      options);
    }
  /**
 * @summary Delete a value no product uses
 */
export const deleteAdminV1OrgAttributesIdValuesValueId = (
    { id, valueId }: DeleteAdminV1OrgAttributesIdValuesValueIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/attributes/${id}/values/${valueId}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Guesses trade-style codes from each value's metadata (purity and colour, grade, carat) or label, unique within the attribute; values that already have a code are left alone. Nothing is rewritten on existing SKUs
 * @summary Fill the SKU codes of values that have none
 */
export const postAdminV1OrgAttributesIdValuesSuggestSkuCodes = (
    { id }: PostAdminV1OrgAttributesIdValuesSuggestSkuCodesPathParameters,
 options?: SecondParameter<typeof customInstance<SuggestSkuCodesResponse>>,) => {
      return customInstance<SuggestSkuCodesResponse>(
      {url: `/admin/v1/org/attributes/${id}/values/suggest-sku-codes`, method: 'POST'
    },
      options);
    }
  export type GetAdminV1OrgAttributesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgAttributes>>>
export type PostAdminV1OrgAttributesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgAttributes>>>
export type GetAdminV1OrgAttributesIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgAttributesId>>>
export type PatchAdminV1OrgAttributesIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgAttributesId>>>
export type DeleteAdminV1OrgAttributesIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgAttributesId>>>
export type PostAdminV1OrgAttributesIdValuesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgAttributesIdValues>>>
export type PatchAdminV1OrgAttributesIdValuesReorderResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgAttributesIdValuesReorder>>>
export type PatchAdminV1OrgAttributesIdValuesValueIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgAttributesIdValuesValueId>>>
export type DeleteAdminV1OrgAttributesIdValuesValueIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgAttributesIdValuesValueId>>>
export type PostAdminV1OrgAttributesIdValuesSuggestSkuCodesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgAttributesIdValuesSuggestSkuCodes>>>
